import React from 'react';
import styled from 'styled-components';
import { type SankeyNode } from '@src/pages/journey-map/types';
import { EventShowcase } from '@src/pages/journey-map/components';

interface NodeLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  node: SankeyNode;
  highlightedNodes: string[];
  selectedNodes: string[];
}

const typeStyles = {
  selected: {
    color: '#fff',
    background: '#1E61F0',
    border: '1px solid #DEDFE2',
    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.2)',
  },
  filter: {
    color: '#1E61F0',
    background: '#F5FAFF',
    border: '1px solid #DEDFE2',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  normal: {
    color: '#000',
    background: '#fff',
    border: '1px solid #DEDFE2',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
};

const Label = styled.div.attrs<{ x: number; y: number; type: string }>((props) => ({
  style: {
    left: `${props.x}px`,
    top: `${props.y}px`,
    ...typeStyles[props.type || 'normal'],
  },
}))<{ x: number; y: number; type: string }>`
  position: absolute;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 1;
  transition: all 0.2s ease;
  text-align: center;
  min-width: 60px;
  z-index: 99;
  cursor: pointer;
  user-select: none;

  &:hover {
    border: 1px solid #1e61f0 !important;
    background: #f5faff !important;
    color: #1e61f0 !important;
  }
`;

const StepBadge = styled.div`
  position: absolute;
  top: -24px;
  height: 20px;
  color: #000;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StyledIndex = styled.div`
  background: #f5faff;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
`;

const NodeLabel: React.FC<NodeLabelProps> = ({
  node,
  highlightedNodes,
  selectedNodes,
  ...rest
}) => {
  const { x1, y0 } = node;
  const nodeX = x1 - 45; // node width
  const nodeY = y0 - 30 - 5; // label height
  const selectedIndex = selectedNodes.findIndex((n) => n === node.ce_id);
  const isSelected = selectedNodes.includes(node.ce_id);
  const isHighlighted = highlightedNodes.includes(node.ce_id);
  const nodeType = isSelected ? 'selected' : isHighlighted ? 'filter' : 'normal';

  return (
    <Label as={'div'} x={nodeX} y={nodeY} type={nodeType} {...rest}>
      {isSelected && (
        <StepBadge>
          Step <StyledIndex>{selectedIndex + 1}</StyledIndex>
        </StepBadge>
      )}
      <EventShowcase
        popoverProps={{
          getPopupContainer: (triggerNode) => document.body,
        }}
        name={node.name}
      />
    </Label>
  );
};

export default NodeLabel;
